import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { SHIFT_STATUS_ONGOING, SHIFT_STATUS_PAUSED, SHIFT_STATUS_VALIDATE } from '../constants.ts';
import { getIntegrations, getShift, getVehicles } from '../queries/getVehicles.ts';
import { Shift } from '../schema/Shift.ts';
import { IVehicle } from '../schema/Vehicle.ts';
import { calculateBearingDegrees, haversineDistanceMeters } from '../utils/geo.ts';

// Below this fix-to-fix distance, GPS jitter dominates over real motion (vehicles
// are polled every 15s, so a stopped/slow vehicle can drift a few meters between
// fixes) — keep the last computed heading instead of recomputing a noisy one.
const MIN_HEADING_UPDATE_DISTANCE_M = 12;

type VehicleFix = { latitude: number; longitude: number; heading: number };

function useVehicleHeadings(vehicles: IVehicle[]): Map<number, number> {
  const lastFixRef = useRef<Map<number, VehicleFix>>(new Map());
  const [headings, setHeadings] = useState<Map<number, number>>(new Map());

  // Ref reads/writes must happen outside render (React Compiler forbids
  // ref access during render), so the cross-render "last fix" cache is
  // updated here and pushed into state instead of computed in useMemo.
  useEffect(() => {
    const nextHeadings = new Map<number, number>();

    for (const vehicle of vehicles) {
      if (vehicle.latitude == null || vehicle.longitude == null) continue;
      const current = { latitude: vehicle.latitude, longitude: vehicle.longitude };
      const last = lastFixRef.current.get(vehicle.id);

      let heading = last?.heading ?? 0;
      if (last && haversineDistanceMeters(last, current) >= MIN_HEADING_UPDATE_DISTANCE_M) {
        heading = calculateBearingDegrees(last, current);
      }

      lastFixRef.current.set(vehicle.id, { ...current, heading });
      nextHeadings.set(vehicle.id, heading);
    }

    setHeadings(nextHeadings);
  }, [vehicles]);

  return headings;
}

export const useShiftByVehicle = (territorySlug: string, vehicleId: number | undefined) => {
  return useQuery({
    queryKey: ['shift', vehicleId],
    queryFn: ({ signal }) => getShift(territorySlug, vehicleId, signal),
    enabled: vehicleId !== undefined,
    throwOnError: true,
  });
};
export const useIntegrationsByVehicle = (territorySlug: string, vehicleId: number | undefined) => {
  return useQuery({
    queryKey: ['integrations', vehicleId],
    queryFn: ({ signal }) => getIntegrations(territorySlug, vehicleId, signal),
    enabled: vehicleId !== undefined,
    throwOnError: true,
  });
};

export const useVehiclesShiftByTerritory = (territorySlug: string) => {
  return useQuery({
    queryKey: ['vehicles', territorySlug],
    queryFn: async ({ signal }) => {
      const vehiclesRes = await getVehicles(territorySlug, signal);
      const shifts = await Promise.all(
        vehiclesRes.map((v: IVehicle) =>
          getShift(territorySlug, v.id, signal).then((s: Shift | null) => ({
            vehicleId: v.id,
            shift: s,
          })),
        ),
      );
      const vehiclesWithShifts = vehiclesRes.map((v: IVehicle) => {
        const s = shifts.find((s: { vehicleId: number; shift: Shift | null }) => s.vehicleId === v.id);
        return { ...v, shift: s?.shift };
      });

      return { ...vehiclesRes, data: vehiclesWithShifts };
    },
    refetchInterval: query => (query.state.error ? false : 15000),
  });
};

export const useGroupedVehiclesByTerritory = (territorySlug: string) => {
  const { data: vehiclesData, ...rest } = useVehiclesShiftByTerritory(territorySlug);
  const rawVehicles: IVehicle[] = useMemo(() => vehiclesData?.data ?? [], [vehiclesData?.data]);
  const headings = useVehicleHeadings(rawVehicles);

  const grouped = useMemo(() => {
    const vehicles = rawVehicles.map(vehicle => ({
      ...vehicle,
      heading: headings.get(vehicle.id) ?? 0,
    }));

    const InOperation: (IVehicle & { heading: number })[] = [];
    const PlannedOperation: (IVehicle & { heading: number })[] = [];
    const Inactive: (IVehicle & { heading: number })[] = [];

    for (const vehicle of vehicles) {
      if (vehicle?.shift && [SHIFT_STATUS_ONGOING].includes(vehicle.shift.status)) {
        InOperation.push(vehicle);
      } else if (
        vehicle?.shift &&
        [SHIFT_STATUS_VALIDATE, SHIFT_STATUS_PAUSED].includes(vehicle.shift.status)
      ) {
        PlannedOperation.push(vehicle);
      } else {
        Inactive.push(vehicle);
      }
    }

    return {
      InOperation,
      PlannedOperation,
      Inactive,
      total: vehicles.length,
    };
  }, [rawVehicles, headings]);

  return {
    ...rest,
    grouped,
  };
};
